import * as React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Copy, ExternalLink, MousePointerClick, Search } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { searchVisits, visitSummary } from "@/api/visits";
import { listDomainsPaged } from "@/api/domains";
import { Pagination } from "@/components/pagination";
import { SideDrawer, DrawerSection, DrawerRow } from "@/components/side-drawer";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatRelativeTime } from "@/lib/utils";
import type { VisitDto } from "@/types/api";

const PAGE_SIZE = 50;

export function VisitsPage(): React.ReactNode {
    const [pageIndex, setPageIndex] = React.useState(0);
    const [domainId, setDomainId] = React.useState<number | "all">("all");
    const [convertedOnly, setConvertedOnly] = React.useState(false);
    const [referrerFilter, setReferrerFilter] = React.useState("");
    const [selectedVisit, setSelectedVisit] = React.useState<VisitDto | null>(null);

    React.useEffect(() => setPageIndex(0), [domainId, convertedOnly, referrerFilter]);

    // Use paged endpoint for the list (so the filter Select can show ALL domains).
    const domainsQuery = useQuery({
        queryKey: ["domains", "for-filter"],
        queryFn: () => listDomainsPaged({ size: 200, sort: "hostname,asc" }),
    });
    const visitsQuery = useQuery({
        queryKey: ["visits", pageIndex, domainId],
        queryFn: () => searchVisits({ page: pageIndex, size: PAGE_SIZE, domainId: domainId === "all" ? undefined : domainId }),
        refetchOnMount: "always",
        staleTime: 0,
        placeholderData: (prev) => prev,
    });
    const summaryQuery = useQuery({
        queryKey: ["visits", "summary"],
        queryFn: () => visitSummary(),
        refetchOnMount: "always",
        staleTime: 0,
    });

    const maxCount = Math.max(0, ...(summaryQuery.data?.map((s) => s.count) ?? []));

    // Client-side refines on the current page (server doesn't support converted-only
    // or referrer-contains yet — fine for now since each page is at most 50 rows).
    const filteredRows = React.useMemo(() => {
        let rows = visitsQuery.data?.content ?? [];
        if (convertedOnly) rows = rows.filter((v) => v.convertedLeadId != null);
        if (referrerFilter) rows = rows.filter((v) => v.referrer?.includes(referrerFilter));
        return rows;
    }, [visitsQuery.data, convertedOnly, referrerFilter]);

    const anyFilterActive = domainId !== "all" || convertedOnly || !!referrerFilter;

    return (
        <>
            <PageHeader
                title="Visits"
                description="Every visitor that hits a landing page. Use this to debug attribution issues before a lead is captured."
            />
            <div className="p-6 grid gap-6 lg:grid-cols-3">
                <Card className="lg:col-span-1">
                    <CardHeader>
                        <CardTitle className="text-base">Top domains</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {summaryQuery.isLoading ? (
                            <Spinner size="sm" />
                        ) : summaryQuery.data && summaryQuery.data.length > 0 ? (
                            <ul className="space-y-3 text-sm">
                                {summaryQuery.data.map((s) => {
                                    const d = (domainsQuery.data?.content ?? []).find((x) => x.id === s.domainId);
                                    const pct = maxCount === 0 ? 0 : (s.count / maxCount) * 100;
                                    return (
                                        <li key={s.domainId} className="space-y-1">
                                            <div className="flex items-center justify-between">
                                                <button
                                                    type="button"
                                                    onClick={() => setDomainId(s.domainId)}
                                                    className="truncate font-medium hover:underline"
                                                    title="Filter the table by this domain"
                                                >
                                                    {d?.hostname ?? `#${s.domainId}`}
                                                </button>
                                                <span className="font-mono text-xs text-muted-foreground">
                                                    {s.count.toLocaleString()}
                                                </span>
                                            </div>
                                            <div className="relative h-2 rounded-full bg-muted">
                                                <div
                                                    className="absolute inset-y-0 left-0 rounded-full bg-primary"
                                                    style={{ width: `${pct}%` }}
                                                />
                                                {pct >= 30 && (
                                                    <span className="absolute inset-y-0 left-2 flex items-center text-[9px] font-medium text-primary-foreground">
                                                        {Math.round(pct)}%
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="text-sm text-muted-foreground">No visits yet.</p>
                        )}
                    </CardContent>
                </Card>

                <div className="lg:col-span-2 space-y-3">
                    {/* Filter bar */}
                    <div className="grid gap-3 sm:grid-cols-3">
                        <div className="space-y-1.5">
                            <Label>Domain</Label>
                            <Select value={domainId === "all" ? "all" : domainId.toString()} onValueChange={(v) => setDomainId(v === "all" ? "all" : Number(v))}>
                                <SelectTrigger data-testid="visit-domain-filter"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All domains</SelectItem>
                                    {(domainsQuery.data?.content ?? []).map((d) => (
                                        <SelectItem key={d.id} value={d.id.toString()}>{d.hostname}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Referrer contains</Label>
                            <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5">
                                <Search className="h-4 w-4 text-muted-foreground" />
                                <Input
                                    value={referrerFilter}
                                    onChange={(e) => setReferrerFilter(e.target.value)}
                                    placeholder="e.g. facebook"
                                    className="h-7 border-0 bg-transparent px-0 focus-visible:ring-0"
                                    data-testid="visit-referrer-filter"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="flex items-center gap-2 pt-1">
                                <input
                                    type="checkbox"
                                    checked={convertedOnly}
                                    onChange={(e) => setConvertedOnly(e.target.checked)}
                                    data-testid="visit-converted-only"
                                />
                                <span>Converted to lead only</span>
                            </Label>
                            <p className="text-[10px] text-muted-foreground">Refines current page only.</p>
                        </div>
                    </div>

                    {visitsQuery.isLoading ? (
                        <div className="flex items-center justify-center py-16">
                            <Spinner size="lg" />
                        </div>
                    ) : filteredRows.length > 0 ? (
                        <>
                            <DataTable<VisitDto>
                                dataTestid="visits-table"
                                columns={[
                                    {
                                        key: "domain",
                                        header: "Domain",
                                        cell: (v) => <span className="text-muted-foreground">{v.domainHostname ?? `#${v.domainId}`}</span>,
                                    },
                                    {
                                        key: "page",
                                        header: "Page",
                                        cell: (v) => v.pageId ? (
                                            <Link to={`/pages/${v.pageId}`} onClick={(e) => e.stopPropagation()} className="text-muted-foreground hover:underline">
                                                {v.pageName ?? `#${v.pageId}`}{v.pageVersionNum != null && <span className="ml-1 text-xs">v{v.pageVersionNum}</span>}
                                            </Link>
                                        ) : <span className="text-muted-foreground">—</span>,
                                    },
                                    {
                                        key: "source",
                                        header: "Source",
                                        cell: (v) =>
                                            v.primarySource ? (
                                                <Badge variant="muted" data-testid="visit-row-source">{v.primarySource}</Badge>
                                            ) : <span className="text-muted-foreground">—</span>,
                                    },
                                    {
                                        key: "country",
                                        header: "Country",
                                        cell: (v) => <span className="text-muted-foreground">{v.country ?? "—"}</span>,
                                    },
                                    {
                                        key: "converted",
                                        header: "Converted",
                                        cell: (v) => v.convertedLeadId ? (
                                            <Link
                                                to={`/leads?id=${v.convertedLeadId}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-900 hover:bg-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300"
                                                title="Open the lead this visit converted to"
                                            >
                                                Lead #{v.convertedLeadId}
                                                <ExternalLink className="h-2.5 w-2.5" />
                                            </Link>
                                        ) : <span className="text-muted-foreground">—</span>,
                                    },
                                    {
                                        key: "created",
                                        header: "When",
                                        cell: (v) => (
                                            <span className="text-muted-foreground" title={formatDate(v.createdAt)}>
                                                {formatRelativeTime(v.createdAt)}
                                            </span>
                                        ),
                                    },
                                ]}
                                rows={filteredRows}
                                rowKey={(v) => v.id}
                                onRowClick={(v) => setSelectedVisit(v)}
                            />
                            {visitsQuery.data && (
                                <Pagination
                                    page={visitsQuery.data}
                                    pageSize={PAGE_SIZE}
                                    currentPage={pageIndex}
                                    onPage={setPageIndex}
                                />
                            )}
                        </>
                    ) : anyFilterActive ? (
                        <EmptyState
                            icon={<MousePointerClick className="h-8 w-8" />}
                            title="No visits match these filters"
                            description="Try clearing one or more filters."
                        />
                    ) : (
                        <EmptyState
                            icon={<MousePointerClick className="h-8 w-8" />}
                            title="No visits yet"
                            description="Once your landing pages start receiving traffic, visits will appear here."
                        />
                    )}
                </div>
            </div>

            <SideDrawer
                open={selectedVisit != null}
                onClose={() => setSelectedVisit(null)}
                dataTestid="visit-detail-drawer"
                title={selectedVisit ? `Visit #${selectedVisit.id}` : ""}
                subtitle={selectedVisit ? formatDate(selectedVisit.createdAt) : ""}
            >
                {selectedVisit && <VisitDetailBody visit={selectedVisit} />}
            </SideDrawer>
        </>
    );
}

function VisitDetailBody({ visit }: { visit: VisitDto }): React.ReactNode {
    return (
        <>
            {/* Related cross-links — most useful action per visit. */}
            <DrawerSection title="Related">
                <div className="flex flex-wrap gap-2 text-xs">
                    {visit.domainHostname && (
                        <span className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-muted-foreground">
                            {visit.domainHostname}
                        </span>
                    )}
                    {visit.pageId && (
                        <Link to={`/pages/${visit.pageId}`} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 hover:bg-accent">
                            <ExternalLink className="h-3 w-3" />
                            {visit.pageName ?? `Page #${visit.pageId}`}
                            {visit.pageVersionNum != null && <span className="text-muted-foreground">v{visit.pageVersionNum}</span>}
                        </Link>
                    )}
                    {visit.campaignId && (
                        <Link to={`/campaigns/${visit.campaignId}`} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 hover:bg-accent">
                            <ExternalLink className="h-3 w-3" />
                            {visit.campaignName ?? `Campaign #${visit.campaignId}`}
                        </Link>
                    )}
                    {visit.convertedLeadId && (
                        <Link to={`/leads?id=${visit.convertedLeadId}`} className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-emerald-900 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300">
                            <ExternalLink className="h-3 w-3" />
                            Converted to Lead #{visit.convertedLeadId}
                        </Link>
                    )}
                </div>
            </DrawerSection>
            <DrawerSection title="Landing URL">
                {visit.landingUrl ? (
                    <button
                        type="button"
                        onClick={() => {
                            void navigator.clipboard.writeText(visit.landingUrl!);
                            toast.success("Copied");
                        }}
                        className="flex w-full items-center gap-2 truncate rounded-md border border-border bg-muted/30 px-2 py-1.5 text-left font-mono text-[11px] hover:bg-muted"
                    >
                        <Copy className="h-3 w-3 shrink-0" />
                        <span className="truncate">{visit.landingUrl}</span>
                    </button>
                ) : <p className="text-xs text-muted-foreground">—</p>}
            </DrawerSection>
            {(visit.clickIds && Object.keys(visit.clickIds).length > 0) && (
                <DrawerSection title="Click IDs">
                    {Object.entries(visit.clickIds).map(([k, v]) => (
                        <DrawerRow key={k} label={k} value={String(v)} />
                    ))}
                </DrawerSection>
            )}
            {(visit.utm && Object.keys(visit.utm).length > 0) && (
                <DrawerSection title="UTM">
                    {Object.entries(visit.utm).map(([k, v]) => (
                        <DrawerRow key={k} label={k} value={String(v)} />
                    ))}
                </DrawerSection>
            )}
            <DrawerSection title="Visitor">
                <DrawerRow label="visitorId" value={visit.visitorId ?? "—"} />
                <DrawerRow label="ip" value={visit.ip ?? "—"} />
                <DrawerRow label="userAgent" value={visit.userAgent ?? "—"} />
                <DrawerRow label="country" value={visit.country ?? "—"} />
                <DrawerRow label="referrer" value={visit.referrer ?? "—"} />
            </DrawerSection>
        </>
    );
}
